import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useController } from 'react-hook-form';
import { formatDate, util } from '../utils/util';
import { FieldControlOmitChildrenProps, replaceNameFieldToField } from './Field';

interface IDatePicker extends FieldControlOmitChildrenProps {
  name: string;
}

// Custom date mask function that doesn't use findDOMNode
const formatDateInput = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Apply mask: DD/MM/YYYY
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

const DatePicker = ({
  label,
  helperText = '',
  id,
  errorMessage,
  name,
  control,
  containerClassName,
  defaultValue,
  ...props
}: IDatePicker & React.ComponentProps<typeof Form.Control>) => {
  const controller = useController({
    name,
    control,
    defaultValue: defaultValue || '',
  });
  const formDefaultValue: string = controller.formState?.defaultValues?.[name] || '';

  useEffect(() => {
    // Only set default value if the field is completely empty (not just falsy)
    if (controller.field.value === undefined || controller.field.value === null) {
      controller.field.onChange(formDefaultValue || '');
    }
  }, [formDefaultValue, controller.field]);

  const [displayValue, setDisplayValue] = useState(() =>
    props.disabled ? formatDate(formDefaultValue) : formatDate(controller.field.value)
  );

  // Update display value when controller value changes
  useEffect(() => {
    if (!props.disabled) {
      setDisplayValue(formatDate(controller.field.value));
    }
  }, [controller.field.value, props.disabled]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.disabled) return;

    const inputValue = e.target.value;
    const maskedValue = formatDateInput(inputValue);
    setDisplayValue(maskedValue);

    if (!util.isDateFormat(maskedValue)) {
      controller.field.onChange(maskedValue);
      return;
    }

    controller.field.onChange(util.dateIsoFormat(maskedValue));
  };

  return (
    <Form.Group className={`mb-3 ${containerClassName}`} controlId={id}>
      <Form.Label>{label}</Form.Label>
      <Form.Control value={displayValue} onChange={handleInputChange} {...props} />
      {helperText && <Form.Text className="text-muted">{helperText}</Form.Text>}
      {errorMessage && (
        <Form.Control.Feedback type="invalid">
          {replaceNameFieldToField(errorMessage?.toString())}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

export default DatePicker;
